'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { Mail, Sparkles, ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export default function ForgotPasswordPage() {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await resetPassword(email.trim());
      setSent(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to send reset link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen hero-gradient flex flex-col items-center justify-center px-4 py-12 relative overflow-hidden">
      {/* Mandala Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] opacity-10 animate-rotate-slow"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='200' cy='200' r='190' fill='none' stroke='%23D4AF37' stroke-width='1'/%3E%3Ccircle cx='200' cy='200' r='150' fill='none' stroke='%23D4AF37' stroke-width='0.8'/%3E%3Ccircle cx='200' cy='200' r='110' fill='none' stroke='%23D4AF37' stroke-width='0.8'/%3E%3Ccircle cx='200' cy='200' r='70' fill='none' stroke='%23D4AF37' stroke-width='0.8'/%3E%3C/svg%3E")`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
          }}
        />
        <div className="absolute top-0 right-0 w-80 h-80 rounded-full bg-saffron/15 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-gold/10 blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-md animate-fade-in-up">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link href="/login" className="inline-flex flex-col items-center gap-3">
            <div className="w-16 h-16 rounded-full saffron-gradient flex items-center justify-center shadow-xl glow-saffron animate-float">
              <span className="text-3xl diya-glow" role="img" aria-label="Diya lamp">🪔</span>
            </div>
            <span className="font-display text-3xl font-bold text-ivory tracking-tight">Yagnick</span>
          </Link>
        </div>

        <div className="glass-dark rounded-3xl p-8 shadow-2xl border border-gold/30">
          {sent ? (
            /* Success State */
            <div className="text-center py-4 animate-scale-in">
              <div className="w-16 h-16 rounded-full bg-green-500/20 border-2 border-green-400/40 flex items-center justify-center mx-auto mb-5">
                <CheckCircle size={32} className="text-green-400" />
              </div>
              <h2 className="font-display text-2xl font-bold text-ivory mb-2">Reset Link Sent!</h2>
              <p className="text-ivory/60 text-sm font-body leading-relaxed mb-6">
                We&apos;ve sent a password reset link to{' '}
                <span className="text-gold font-semibold">{email}</span>.
                Please check your inbox.
              </p>
              <Link
                href="/login"
                className="inline-flex items-center gap-2 text-gold hover:text-saffron text-sm font-semibold font-body transition-colors"
              >
                <ArrowLeft size={16} />
                Back to Login
              </Link>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="text-center mb-7">
                <div className="inline-flex items-center gap-2 bg-gold/20 border border-gold/30 text-gold text-xs font-medium px-3 py-1.5 rounded-full mb-3 tracking-wider uppercase">
                  <Sparkles size={11} />
                  Password Reset
                </div>
                <h1 className="font-display text-3xl font-bold text-ivory">Forgot Password?</h1>
                <p className="text-ivory/60 text-sm mt-2 font-body leading-relaxed">
                  Enter your registered email and we&apos;ll send you a secure reset link.
                </p>
              </div>

              {/* Error */}
              {error && (
                <div className="mb-5 bg-red-900/30 border border-red-500/40 rounded-2xl px-4 py-3 animate-scale-in">
                  <p className="text-red-300 text-sm font-body">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                <div>
                  <label className="block text-ivory/80 text-sm font-medium mb-2 font-body">Email Address</label>
                  <div className="relative">
                    <Mail size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-gold/60" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setError(''); }}
                      placeholder="you@example.com"
                      className="w-full bg-white/8 border border-gold/25 hover:border-gold/50 focus:border-gold text-ivory placeholder-ivory/30 rounded-2xl pl-11 pr-4 py-3.5 text-sm font-body outline-none transition-all duration-200 focus:bg-white/12"
                      autoComplete="email"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full saffron-gradient text-white font-semibold py-4 rounded-2xl btn-glow flex items-center justify-center gap-2 text-base disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      Send Reset Link
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </form>

              <div className="flex items-center justify-center mt-6">
                <Link
                  href="/login"
                  className="inline-flex items-center gap-2 text-ivory/50 hover:text-gold text-sm font-body transition-colors"
                >
                  <ArrowLeft size={15} />
                  Back to Login
                </Link>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-ivory/30 text-xs mt-6 font-body">
          🙏 Connecting devotees with verified priests across India
        </p>
      </div>
    </div>
  );
}
